import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Camera, Check, ChevronRight, FileText, Globe2, History, Info, LogOut, Pencil, ShieldQuestion } from 'lucide-react';
import Header from '../components/layout/Header';
import ErrorMessage from '../components/common/ErrorMessage';
import { useAuth } from '../components/auth/AuthContext';
import { countryOptions } from '../services/countries';
import { api } from '../services/api';

function buildSections(signalementCount) {
  return [
    { title: 'Mon compte', items: [{ icon: History, label: 'Historique' }, { icon: FileText, label: 'Rapports PDF' }, { icon: ShieldQuestion, label: 'Mes signalements', badge: signalementCount > 0 ? String(signalementCount) : null }] },
    { title: 'Préférences', items: [{ icon: Bell, label: 'Notifications', toggle: true }, { icon: Globe2, label: 'Langue', value: 'Français' }] },
  { title: 'Informations', items: [{ icon: Info, label: 'Comment ça marche' }, { icon: ShieldQuestion, label: 'Politique de confidentialité', value: 'Bientôt' }, { icon: Info, label: 'Version', value: 'v1.0.0' }] },
  ];
}

const profileRoutes = {
  Historique: '/history',
  'Rapports PDF': '/reports',
  'Mes signalements': '/signalements',
  'Comment ça marche': '/how-it-works',
};

export default function ProfilePage() {
  const { user, logout, updateProfile, updateProfilePhoto } = useAuth();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', country: 'Bénin' });
  const [error, setError] = useState('');
  const [notifications, setNotifications] = useState(true);
  const [analysisCount, setAnalysisCount] = useState(0);
  const [signalementCount, setSignalementCount] = useState(0);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const currentUser = user || { name: 'Utilisateur SENTINELLE', email: '', country: 'Bénin', initials: 'US' };

  useEffect(() => {
    setForm({ name: currentUser.name || '', email: currentUser.email || '', country: currentUser.country || 'Bénin' });
  }, [currentUser.country, currentUser.email, currentUser.name]);

  // Charger les stats réelles depuis la BDD
  useEffect(() => {
    api.getStats().then((stats) => {
      setAnalysisCount(stats.analyses);
      setSignalementCount(stats.signalements);
    });
  }, []);

  const sections = buildSections(signalementCount);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const handlePhoto = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => updateProfilePhoto(reader.result);
    reader.readAsDataURL(file);
  };

  const saveProfile = () => {
    if (!form.name.trim() || !form.email.trim()) {
      setError('Le nom et le mail sont nécessaires pour enregistrer votre profil.');
      return;
    }
    updateProfile(form);
    setEditing(false);
    setError('');
  };

  return (
    <div className="page-container">
      <Header />
      <main className="md:grid md:grid-cols-[320px_1fr] md:gap-6 md:items-start md:pt-6">
        <section className="mx-4 mt-6 md:mt-0 card text-center py-6">
          <div className="relative w-24 h-24 mx-auto">
            <div className="w-24 h-24 rounded-full bg-brand-navy text-white flex items-center justify-center text-2xl font-bold shadow-lg overflow-hidden">
              {currentUser.avatar ? <img src={currentUser.avatar} alt="Profil" className="w-full h-full object-cover" /> : currentUser.initials}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
            <button type="button" onClick={() => fileInputRef.current?.click()} className="absolute -right-1 bottom-1 w-10 h-10 rounded-full bg-accent-blue text-white flex items-center justify-center border-4 border-white shadow-md active:scale-95 transition-transform" aria-label="Choisir une photo de profil">
              <Camera className="w-5 h-5" />
            </button>
          </div>
          <h1 className="mt-5 font-bold text-xl text-text-primary">{currentUser.name}</h1>
          <p className="text-text-muted break-all">{currentUser.email}</p>
          <p className="mt-1 text-sm text-accent-blue">{currentUser.country}</p>
          <button type="button" onClick={() => setEditing((value) => !value)} className="mt-4 min-h-11 text-accent-blue font-medium inline-flex items-center gap-2 active:scale-95 transition-transform" aria-expanded={editing}>
            <Pencil className="w-4 h-4" /> Modifier le profil
          </button>
        </section>

        <section>
          {editing ? <ProfileEditor form={form} setForm={setForm} onSave={saveProfile} error={error} /> : null}
          <div className="mx-4 mt-6 grid grid-cols-3 gap-3">
            <Stat value={String(analysisCount)} label="Analyses" />
            <Stat value={String(signalementCount)} label="Signalements" />
            <Stat value={(currentUser.country || 'BJ').slice(0, 2).toUpperCase()} label={currentUser.country || 'Pays'} />
          </div>
          {sections.map((section) => <ProfileSection key={section.title} section={section} notifications={notifications} setNotifications={setNotifications} />)}
          <button type="button" onClick={handleLogout} className="mx-auto mt-8 min-h-11 text-verdict-danger font-semibold flex items-center gap-2 active:scale-95 transition-transform" aria-label="Se déconnecter du compte">
            <LogOut className="w-5 h-5" /> Se déconnecter
          </button>
        </section>
      </main>
    </div>
  );
}

function ProfileEditor({ form, setForm, onSave, error }) {
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  return (
    <div className="mx-4 mt-6 card space-y-4">
      <Field label="Nom" value={form.name} onChange={(value) => update('name', value)} />
      <Field label="Mail" type="email" value={form.email} onChange={(value) => update('email', value)} />
      <label className="block">
        <span className="text-sm font-semibold text-text-primary">Pays</span>
        <select value={form.country} onChange={(event) => update('country', event.target.value)} className="mt-2 input-field font-sans">
          {countryOptions.map((country) => <option key={country.value} value={country.value}>{country.label}</option>)}
        </select>
      </label>
      {error ? <ErrorMessage message={error} /> : null}
      <button type="button" onClick={onSave} className="btn-primary"><Check className="w-5 h-5" /> Enregistrer</button>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return <label className="block"><span className="text-sm font-semibold text-text-primary">{label}</span><input type={type} value={value} onChange={(event) => onChange(event.target.value)} className="mt-2 input-field font-sans" /></label>;
}

function Stat({ value, label }) {
  return <div className="card text-center py-4"><p className="font-mono font-bold text-xl text-brand-navy">{value}</p><p className="text-[10px] uppercase tracking-wider text-text-muted mt-1 truncate">{label}</p></div>;
}

function ProfileSection({ section, notifications, setNotifications }) {
  const navigate = useNavigate();
  const handleItem = (label) => {
    if (label === 'Notifications') setNotifications((value) => !value);
    if (profileRoutes[label]) navigate(profileRoutes[label]);
  };

  return (
    <section className="mx-4 mt-7">
      <h2 className="text-xs uppercase tracking-widest text-text-muted mb-3">{section.title}</h2>
      <div className="card p-0 overflow-hidden">
        {section.items.map(({ icon: Icon, label, value, badge, toggle }) => {
          const interactive = toggle || profileRoutes[label];
          const content = <RowContent Icon={Icon} label={label} value={value} badge={badge} toggle={toggle} notifications={notifications} />;
          if (!interactive) return <div key={label} className="w-full min-h-[64px] px-4 flex items-center gap-4 border-b last:border-b-0 border-gray-100 text-left">{content}</div>;
          return (
            <button key={label} type="button" onClick={() => handleItem(label)} role={toggle ? 'switch' : undefined} aria-checked={toggle ? notifications : undefined} className="w-full min-h-[64px] px-4 flex items-center gap-4 border-b last:border-b-0 border-gray-100 text-left active:bg-gray-50 transition-colors" aria-label={toggle ? `${notifications ? 'Désactiver' : 'Activer'} les notifications` : label}>
              {content}
            </button>
          );
        })}
      </div>
    </section>
  );
}

function RowContent({ Icon, label, value, badge, toggle, notifications }) {
  return (
    <>
      <span className="w-10 h-10 rounded-xl bg-gray-100 text-brand-navy flex items-center justify-center"><Icon className="w-5 h-5" /></span>
      <span className="flex-1 text-text-primary">{label}</span>
      {badge ? <span className="w-6 h-6 rounded-full bg-verdict-danger text-white text-xs flex items-center justify-center">{badge}</span> : null}
      {toggle ? <span className={`w-12 h-7 rounded-full p-1 flex transition-colors ${notifications ? 'bg-brand-navy justify-end' : 'bg-gray-300 justify-start'}`}><span className="w-5 h-5 rounded-full bg-white" /></span> : null}
      {value ? <span className="text-accent-blue">{value}</span> : <ChevronRight className="w-5 h-5 text-text-muted" />}
    </>
  );
}
